<template>
  <div>
    <div class="admin-toolbar">
      <p class="admin-subtitle">Overview of the website at a glance.</p>
    </div>

    <p v-if="error" class="card mb-5 border-red-100 bg-red-50 p-4 text-sm text-red-700">{{ error }}</p>

    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      <router-link
        v-for="stat in stats"
        :key="stat.label"
        :to="stat.to"
        class="card group flex items-center gap-4 p-5 transition-shadow hover:shadow-card-hover"
      >
        <span :class="['flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md text-lg', stat.tint]">
          <i :class="['fa-solid', stat.icon]"></i>
        </span>
        <span class="min-w-0 flex-1">
          <span class="block text-sm font-medium text-gray-500">{{ stat.label }}</span>
          <span class="block text-3xl font-bold tabular-nums text-gray-900">
            <span v-if="loading" class="inline-block h-7 w-16 animate-pulse rounded bg-gray-100 align-middle"></span>
            <template v-else>{{ Number(stat.value || 0).toLocaleString('en-IN') }}</template>
          </span>
        </span>
        <i class="fa-solid fa-arrow-right text-xs text-gray-300 transition-all group-hover:translate-x-0.5 group-hover:text-brand-red"></i>
      </router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import api from '@/api'

const loading = ref(true)
const error = ref('')
const counts = ref({})

const stats = computed(() => [
  { label: 'Total Views', value: counts.value.views, icon: 'fa-eye', tint: 'bg-blue-50 text-blue-600', to: '/admin/dashboard' },
  { label: 'Total Newspapers', value: counts.value.newspapers, icon: 'fa-newspaper', tint: 'bg-amber-50 text-amber-600', to: '/admin/newspaper' },
  { label: 'Total Magazines', value: counts.value.magazines, icon: 'fa-book-open', tint: 'bg-red-50 text-brand-red', to: '/admin/magazine' },
  { label: 'Total Users', value: counts.value.users, icon: 'fa-users', tint: 'bg-emerald-50 text-emerald-600', to: '/admin/users' },
  { label: 'Total Artists', value: counts.value.artists, icon: 'fa-palette', tint: 'bg-purple-50 text-purple-600', to: '/admin/artist' },
  { label: 'Contact Messages', value: counts.value.contacts, icon: 'fa-envelope', tint: 'bg-rose-50 text-rose-600', to: '/admin/contact' },
])

onMounted(async () => {
  try {
    const [viewsRes, usersRes, artistsRes, contactsRes, newspapersRes, magazineRes] = await Promise.all([
      api.get('/views'),
      api.get('/users/count'),
      api.get('/artists/count'),
      api.get('/contacts/count'),
      api.get('/newspapers/count'),
      api.get('/magazines/count'),
    ])

    counts.value = {
      views: viewsRes.data.total_views,
      users: usersRes.data.total_users,
      artists: artistsRes.data.total_artists,
      contacts: contactsRes.data.total_contacts,
      newspapers: newspapersRes.data.totalNewspapers,
      magazines: magazineRes.data.total,
    }
  } catch (err) {
    console.error('Error fetching dashboard data:', err)
    error.value = 'Could not load the dashboard numbers. Please refresh.'
  } finally {
    loading.value = false
  }
})
</script>
